import { defineStore } from "pinia";
import { rootGetters } from "@/main";

import { moveItemToFront } from "@/services/utils/Arrays"
import { useBrandsStore as useBrandsDictionaryStore } from "@/store/catalog_data/brands";

const endpoints = {
    getBrands: { method: "get", url: "/catalog_data/brands" },
    getBrand: { method: "get", url: "/catalog_data/brands/{brand_pk}" },
    deleteBrand: { method: "delete", url: "/catalog_data/brands/{brand_pk}" },
    createBrand: { method: "post", url: "/catalog_data/brands" },
    updateBrand: { method: "put", url: "/catalog_data/brands/{brand_pk}" },
};

export const useBrandsStore = defineStore("brands", {
    state: () => ({
        brandList: [] as BaseBrand[],
        brandCount: 0,
        brandPerPage: 1,
        activeFirst: false,
        addedId: 0,
    }),
    getters: {
        brandsPagesCount: (state) => {
            return Math.ceil(state.brandCount / state.brandPerPage)
        }
    },
    actions: {
        async createBrand(brand: BrandForCreate) {
            await rootGetters.api
                .proceedRequest(endpoints.createBrand, {}, {}, brand)
                .then((response) => {
                    if (response) {
                        this.addedId = response.data.id;
                        this.activeFirst = true;

                        // Обновляем словарь брендов
                        const brandsDictionaryStore = useBrandsDictionaryStore();
                        brandsDictionaryStore.addOrUpdateItem({
                            brand_pk: response.data.brand_pk,
                            brand_name: response.data.brand_name
                        });

                        rootGetters.toast.show(
                            "info",
                            "Создание",
                            `Бренд "${response.data.brand_name}" успешно сохранен в раздел "Список брендов".`
                        );
                    }
                }).catch((err) => {
                    return Promise.reject(err);
                })
        },
        async getBrand(brandPk: string) {
            return await rootGetters.api
                .proceedRequest(endpoints.getBrand, { brand_pk: brandPk })
                .then((response) => {
                    return response.data;
                });
        },
        async getBrands(params: BrandsParams = { page: 1, per_page: 20 }) {
            await rootGetters.api.proceedRequest(
                endpoints.getBrands, {}, params
            ).then((response) => {
                this.brandList = response.data;
                if (this.brandList) {
                    this.brandPerPage = parseInt(response.headers["x-per-page"]);
                    this.brandCount = parseInt(response.headers["x-total-count"]);

                    if (this.addedId > 0) {
                        moveItemToFront(this.brandList, this.addedId);
                        this.addedId = 0;
                    }
                }
            });
        },
        async updateBrand(brand: BrandForCreate, brandPk: string) {
            await rootGetters.api
                .proceedRequest(endpoints.updateBrand, { brand_pk: brandPk }, {}, brand)
                .then((response) => {
                    if (response) {
                        this.addedId = response.data.id;
                        this.activeFirst = true;

                        rootGetters.toast.show(
                            "info",
                            "Обновление",
                            `Бренд "${response.data.brand_name}" успешно обновлен.`
                        );
                    }
                }).catch((err) => {
                    return Promise.reject(err);
                })
        },
        async deleteBrand(brandPk: string) {
            await rootGetters.api
                .proceedRequest(endpoints.deleteBrand, {
                    brand_pk: brandPk,
                })
        },
    }
})

export interface BaseBrand {
    brand_pk: string;
    brand_name: string;
    brand_patterns: BrandPattern[];
    categories: BrandCategory[];
    is_used_in_wbparser: boolean;
    is_used_in_datalens: boolean;
}

export interface BrandPattern {
    pattern: string;
}

export interface BrandCategory {
    id: number,
    name: string,
    level: string,
    full_name: string,
    parent_id: number
}

export interface BrandForCreate {
    brand: BrandData;
    brand_patterns: BrandPattern[];
    categories: BrandCategoryCreate[];
}

interface BrandData {
    brand_pk: string;
    brand_name: string;
    is_used_in_wbparser: boolean;
    is_used_in_datalens: boolean;
}

export interface BrandCategoryCreate {
    category_id: number;
}


export interface BrandsParams {
    page: number;
    per_page: number;

    name?: string;
    brand_name__ilike?: string;
    cdate?: string;
    mdate?: string;
    cuser__id__in?: string;
    categories__category_id__in?: string;
    patterns__pattern__ilike?: string;

    is_used_in_wbparser?: boolean;
    is_used_in_datalens?: boolean;

    order_by?: string;
}

